import { useState, useMemo } from 'preact/hooks';
import { useModels } from '../hooks/useModels';
import { useAllSessions } from '../state/sessionListStore';
import { shortLabel } from '../utils/modelHelpers';

export interface ExplainPayload {
  text: string;
  sourceEl: Element;
}

interface Props {
  payload: ExplainPayload;
  currentSessionId: string | null;
  onHere: () => void;
  onSession: (sessionId: string) => void;
  onNewSession: (modelId: string) => void;
  onClose: () => void;
}

const PROVIDER_LABEL: Record<string, string> = {
  anthropic: 'Anthropic',
  google: 'Google',
  ollama: 'Local (Ollama)',
};

const PROVIDER_COLOR: Record<string, string> = {
  anthropic: '#b088f0',
  google: '#34a853',
  ollama: '#f5a623',
};

export function ExplainTargetModal({ payload, currentSessionId, onHere, onSession, onNewSession, onClose }: Props) {
  const sessions = useAllSessions();
  const { byProvider, loading: modelsLoading } = useModels();
  const [selectedModel, setSelectedModel] = useState<string | null>(null);

  const otherSessions = useMemo(
    () => sessions.filter((s) => s.status !== 'cleared' && s.id !== currentSessionId),
    [sessions, currentSessionId],
  );

  const allProviders: { key: string; models: typeof byProvider.anthropic }[] = useMemo(() => {
    const entries: { key: string; models: typeof byProvider.anthropic }[] = [];
    if (byProvider.anthropic.length > 0) entries.push({ key: 'anthropic', models: byProvider.anthropic });
    if (byProvider.google.length > 0) entries.push({ key: 'google', models: byProvider.google });
    if (byProvider.ollama && byProvider.ollama.length > 0) entries.push({ key: 'ollama', models: byProvider.ollama });
    return entries;
  }, [byProvider]);

  const preview = payload.text.length > 120 ? payload.text.slice(0, 120) + '…' : payload.text;

  function handleStartNewSession() {
    const model = selectedModel ?? byProvider.anthropic.find((m) => m.isDefault)?.modelId ?? byProvider.anthropic[0]?.modelId;
    if (!model) return;
    onNewSession(model);
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.72)',
        zIndex: 9999,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '0 0 env(safe-area-inset-bottom, 0)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--bg-card, #1e1e2e)',
          border: '1px solid var(--border, #313244)',
          borderRadius: '16px 16px 0 0',
          width: '100%',
          maxWidth: 520,
          maxHeight: '90svh',
          overflowY: 'auto',
          padding: '20px 16px 28px',
          color: 'var(--fg, #cdd6f4)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontWeight: 700, fontSize: '1rem' }}>Ask about this</span>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--fg-muted, #a6adc8)', fontSize: '1.2rem', cursor: 'pointer', padding: '2px 6px', lineHeight: 1 }}
            aria-label="Close"
          >✕</button>
        </div>

        {/* Selection preview */}
        <div style={{
          background: 'var(--bg-surface, #181825)',
          border: '1px solid var(--border, #313244)',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.8rem',
          color: 'var(--fg-muted, #a6adc8)',
          lineHeight: 1.45,
          marginBottom: 18,
          fontStyle: 'italic',
          maxHeight: 72,
          overflow: 'hidden',
        }}>
          "{preview}"
        </div>

        {/* ── HERE CTA ── */}
        <button
          onClick={onHere}
          style={{
            width: '100%',
            padding: '13px',
            borderRadius: 10,
            border: 'none',
            background: 'var(--accent, #89b4fa)',
            color: '#1e1e2e',
            fontWeight: 700,
            fontSize: '0.95rem',
            cursor: 'pointer',
            marginBottom: 20,
          }}
        >
          🔍 Explain here
        </button>

        {/* ── OTHER SESSIONS ── */}
        {otherSessions.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.7px', textTransform: 'uppercase', color: 'var(--fg-muted, #a6adc8)', marginBottom: 8 }}>
              Send to another session
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {otherSessions.map((s) => {
                const name = s.name || s.previewText || 'Untitled session';
                const truncated = name.length > 52 ? name.slice(0, 52) + '…' : name;
                return (
                  <button
                    key={s.id}
                    onClick={() => onSession(s.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: 'var(--bg-surface, #181825)',
                      border: '1px solid var(--border, #313244)',
                      borderRadius: 8,
                      padding: '9px 12px',
                      cursor: 'pointer',
                      color: 'var(--fg, #cdd6f4)',
                      fontSize: '0.85rem',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.status === 'running' ? '#a6e3a1' : 'var(--fg-muted, #a6adc8)', flexShrink: 0 }} />
                    {truncated}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── NEW SESSION ── */}
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.7px', textTransform: 'uppercase', color: 'var(--fg-muted, #a6adc8)', marginBottom: 8 }}>
            Start a new session
          </div>

          {modelsLoading ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--fg-muted, #a6adc8)', padding: '8px 4px' }}>Loading models…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {allProviders.map(({ key, models }) => (
                <div key={key}>
                  <div style={{
                    fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.7px', textTransform: 'uppercase',
                    color: PROVIDER_COLOR[key] ?? 'var(--fg-muted, #a6adc8)',
                    padding: '4px 4px 4px 0', marginBottom: 2,
                  }}>
                    {PROVIDER_LABEL[key] ?? key}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {models.map((m) => {
                      const sl = shortLabel(m);
                      const isSelected = selectedModel === m.modelId || (!selectedModel && m.isDefault && key === 'anthropic');
                      return (
                        <button
                          key={m.modelId}
                          onClick={() => setSelectedModel(m.modelId)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: isSelected ? 'var(--accent-muted, rgba(137,180,250,0.15))' : 'var(--bg-surface, #181825)',
                            border: isSelected ? '1px solid var(--accent, #89b4fa)' : '1px solid var(--border, #313244)',
                            borderRadius: 7,
                            padding: '8px 10px',
                            cursor: 'pointer',
                            color: 'var(--fg, #cdd6f4)',
                            fontSize: '0.85rem',
                            textAlign: 'left',
                          }}
                        >
                          <span style={{
                            width: 14, height: 14, borderRadius: '50%',
                            border: isSelected ? '4px solid var(--accent, #89b4fa)' : '2px solid var(--fg-muted, #a6adc8)',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                          }} />
                          <span style={{ flex: 1 }}>{sl.label}</span>
                          {sl.tag && <span style={{ fontSize: '0.7rem', color: 'var(--fg-muted, #a6adc8)', background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: 4 }}>{sl.tag}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleStartNewSession}
            disabled={modelsLoading || allProviders.length === 0}
            style={{
              width: '100%',
              padding: '11px',
              borderRadius: 10,
              border: '1px solid var(--accent, #89b4fa)',
              background: 'transparent',
              color: 'var(--accent, #89b4fa)',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
              opacity: modelsLoading ? 0.5 : 1,
            }}
          >
            Start &amp; explain
          </button>
        </div>
      </div>
    </div>
  );
}
